import { useState } from 'react';

import {
	type DefaultMessagesResponse,
	type FrontendStage,
	type PlacementMessageBackendFactory,
	type PlacementMessageFrontendFactory,
	createPlacement,
} from '@post-office/placement-common-v2';
import { type CustomRenderer } from '@post-office/placement-common-v2/types';
import type { EligibleMessage } from '@post-office/shared-contracts/shared-request-middleware-context/context';
import { usePlacementApi } from '@post-office/use-placement-api';
import {
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type DefaultPlacementBackend,
	type BackendSyncMessageStage,
	type AnyPayload,
	type AnyBackendMessageStage,
} from '@post-office/placement-common-v2/create-placement/types';
import { type TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';

export type PlacementRenderProps = {
	onDismiss: () => void;
	onClick?: () => void;
};

type PlacementDefinition = CustomRenderer<PlacementRenderProps>;

type PlacementFrontendProps = {
	preloadedInitialData?: DefaultMessagesResponse;
	onClick?: () => void;
};

const placement = createPlacement<PlacementDefinition>({
	placementId: 'operations-overview-discoverability-spotlights',
	options: {
		choreographer: {
			isDefaultEnabled: true,
		},
		// hideInIsolatedCloud: true, // For FedRAMP
	},
});
export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<PlacementRenderProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	U
> = placement.createFrontend;
export const createBackend: (
	placementBackendParams?:
		| DefaultPlacementBackend<{
				transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
				hydrate?: AnyBackendMessageStage;
		  }>
		| undefined,
) => PlacementMessageBackendFactory<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = placement.createBackend;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	PlacementFrontendProps
> = createFrontend<PlacementFrontendProps>((Messages) => ({ preloadedInitialData, onClick }) => {
	const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

	const { data, error, isLoading } = usePlacementApi<{
		messages: Array<EligibleMessage>;
	}>('/', {
		autoRefetch: {
			isEnabled: !preloadedInitialData,
			updateKey: 'operations-overview-discoverability-spotlights',
		},
	});

	const messages = preloadedInitialData?.messages || data?.messages;

	const visibleMessages = messages
		?.filter((message) => !dismissedIds.has(message.messageInstanceId))
		?.slice?.(0, 1); // Only show one message at most

	const handleDismiss = (messageInstanceId: string) => {
		setDismissedIds((prev) => new Set([...Array.from(prev), messageInstanceId]));
	};

	if (error) {
		throw error;
	}

	if (!preloadedInitialData && isLoading) {
		return null;
	}

	if (!visibleMessages || visibleMessages.length === 0) {
		return null;
	}

	return (
		<>
			{visibleMessages.map((message) => (
				<Messages.render
					key={message.messageInstanceId}
					onDismiss={() => handleDismiss(message.messageInstanceId)}
					onClick={onClick}
					{...message}
				/>
			))}
		</>
	);
});

export const PlacementBackend: PlacementMessageBackendFactory<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = createBackend();
